/**
 * QuickSwitcher — Ctrl/Cmd+O fuzzy file switcher (FR-2.4).
 *
 * A modal fed from the already-fetched vault tree: type to filter, arrow
 * keys to move, Enter to open, Escape to dismiss. Matching is a pragmatic
 * subsequence match (all typed characters must appear in order in the
 * path), with exact-substring and basename hits ranked first — the same
 * behaviour users expect from Obsidian's own switcher without pulling in
 * a fuzzy-search dependency.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

export interface QuickSwitcherProps {
  /** Vault-relative file paths, e.g. "/notes/a.md". */
  readonly files: readonly string[];
  readonly onPick: (path: string) => void;
  readonly onClose: () => void;
}

export interface QuickSwitcherMatch {
  readonly path: string;
  readonly score: number;
}

/** Case-insensitive subsequence test. */
function isSubsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) i += 1;
    if (i === needle.length) return true;
  }
  return needle.length === 0;
}

/**
 * Rank vault files against a query. Exported for tests.
 *  - empty query → first `limit` files in tree order
 *  - basename substring match ranks above path substring match,
 *    which ranks above bare subsequence match
 */
export function filterQuickSwitcher(
  files: readonly string[],
  query: string,
  limit = 50,
): readonly string[] {
  const q = query.trim().toLowerCase();
  if (q === '') return files.slice(0, limit);
  const matches: QuickSwitcherMatch[] = [];
  for (const path of files) {
    const lower = path.toLowerCase();
    const base = lower.slice(lower.lastIndexOf('/') + 1);
    let score: number | null = null;
    if (base.startsWith(q)) score = 0;
    else if (base.includes(q)) score = 1;
    else if (lower.includes(q)) score = 2;
    else if (isSubsequence(q, lower)) score = 3;
    if (score !== null) matches.push({ path, score });
  }
  matches.sort((a, b) => a.score - b.score || a.path.localeCompare(b.path));
  return matches.slice(0, limit).map((m) => m.path);
}

export function QuickSwitcher(props: QuickSwitcherProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hits = useMemo(() => filterQuickSwitcher(props.files, query), [props.files, query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clamp the selection when the hit list shrinks.
  const sel = Math.min(selected, Math.max(hits.length - 1, 0));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      props.onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(Math.min(sel + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(Math.max(sel - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = hits[sel];
      if (pick !== undefined) {
        props.onPick(pick);
        props.onClose();
      }
    }
  };

  return (
    <div
      data-testid="quick-switcher-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Quick switcher"
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
        zIndex: 60,
      }}
    >
      <div
        data-testid="quick-switcher"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 90vw)',
          background: 'var(--background-primary, #1e1e1e)',
          border: '1px solid var(--background-modifier-border, #333)',
          borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          data-testid="quick-switcher-input"
          type="text"
          placeholder="Type to find a file… (Enter to open, Esc to close)"
          aria-label="Quick switcher query"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(0);
          }}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            background: 'var(--background-primary, #1e1e1e)',
            color: 'var(--text-normal, #dcddde)',
            border: 'none',
            borderBottom: '1px solid var(--background-modifier-border, #333)',
            outline: 'none',
            font: 'inherit',
          }}
        />
        <ul
          data-testid="quick-switcher-results"
          style={{ listStyle: 'none', margin: 0, padding: '4px 0', maxHeight: '50vh', overflow: 'auto' }}
        >
          {hits.length === 0 ? (
            <li style={{ padding: '8px 12px', color: 'var(--text-muted, #999)' }}>No matches</li>
          ) : (
            hits.map((path, i) => (
              <li key={path}>
                <button
                  type="button"
                  data-testid="quick-switcher-item"
                  aria-selected={i === sel}
                  onMouseEnter={() => setSelected(i)}
                  onClick={() => {
                    props.onPick(path);
                    props.onClose();
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 12px',
                    background: i === sel ? 'var(--background-modifier-hover, #2a2a2a)' : 'none',
                    color: 'inherit',
                    border: 'none',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {path}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
